import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Plus, Search, Calendar, User, Building, DollarSign, X, Edit, Eye, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Leases() {
  const [leases, setLeases] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filterProperty, setFilterProperty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [selectedLease, setSelectedLease] = useState(null);
  const [formData, setFormData] = useState({
    entity_id: '', unit_id: '', tenant_name: '', tenant_email: '', tenant_phone: '',
    lease_type: 'residential', start_date: '', end_date: '', monthly_rent: '', security_deposit: ''
  });

  useEffect(() => { fetchReferenceData(); }, []);

  useEffect(() => { fetchLeases(); }, [pagination.page, filterProperty, filterStatus]);

  const fetchReferenceData = async () => {
    try {
      const [propsRes, unitsRes] = await Promise.all([
        axios.get(`${API}/corporations`), axios.get(`${API}/units`)
      ]);
      setProperties(propsRes.data.data || propsRes.data);
      setUnits(unitsRes.data.data || unitsRes.data);
    } catch (error) { console.error('Failed to fetch reference data:', error); }
  };

  const fetchLeases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filterProperty) params.append('entity_id', filterProperty);
      if (filterStatus) params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/leases?${params}`);
      
      // Handle both paginated and non-paginated responses
      if (res.data.data) {
        setLeases(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        // Fallback for non-paginated response
        setLeases(res.data);
        setPagination(prev => ({ ...prev, total: res.data.length, pages: 1 }));
      }
    } catch (error) { console.error('Failed to fetch leases:', error); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, filterProperty, filterStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, monthly_rent: parseFloat(formData.monthly_rent), security_deposit: parseFloat(formData.security_deposit) || 0 };
      if (editingLease) {
        await axios.put(`${API}/leases/${editingLease.id}`, data);
      } else {
        await axios.post(`${API}/leases`, data);
      }
      setShowModal(false);
      resetForm();
      fetchLeases();
    } catch (error) { console.error('Failed to save lease:', error); }
  };

  const openEditModal = (lease) => {
    setEditingLease(lease);
    setFormData({
      entity_id: lease.entity_id || '', unit_id: lease.unit_id || '',
      tenant_name: lease.tenant_name || '', tenant_email: lease.tenant_email || '', tenant_phone: lease.tenant_phone || '',
      lease_type: lease.lease_type || 'residential', start_date: lease.start_date || '', end_date: lease.end_date || '',
      monthly_rent: lease.monthly_rent || lease.monthly_maintenance_fee || '', security_deposit: lease.security_deposit || ''
    });
    setShowModal(true);
  };

  const openViewModal = (lease) => { setSelectedLease(lease); setShowViewModal(true); };

  const resetForm = () => {
    setEditingLease(null);
    setFormData({ entity_id: '', unit_id: '', tenant_name: '', tenant_email: '', tenant_phone: '', lease_type: 'residential', start_date: '', end_date: '', monthly_rent: '', security_deposit: '' });
  };

  const handleFilterChange = (type, value) => {
    if (type === 'property') setFilterProperty(value);
    if (type === 'status') setFilterStatus(value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filtering
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Use leases directly since filtering is now done server-side
  const filteredLeases = leases;

  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(amount) : '$0';
  const getDaysUntilExpiry = (endDate) => { const diff = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24)); return diff; };

  if (loading && leases.length === 0) { return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>; }

  // Use filteredLeases for stats to reflect current page
  const activeLeases = filteredLeases.filter(l => l.status === 'active').length;
  const totalMonthly = filteredLeases.filter(l => l.status === 'active').reduce((sum, l) => sum + (l.total_monthly || 0), 0);
  const expiringSoon = filteredLeases.filter(l => getDaysUntilExpiry(l.end_date) <= 90 && getDaysUntilExpiry(l.end_date) > 0).length;

  // Dynamic label based on filter
  const statsLabel = filterProperty ? 'in Property' : 'Total';

  return (
    <div className="space-y-6" data-testid="leases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-slate-900">Leases</h1><p className="text-slate-500">Manage residential and commercial leases</p></div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700" data-testid="new-lease-btn">
          <Plus className="w-4 h-4" /> New Lease
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Total Leases</p><p className="text-2xl font-bold text-slate-900">{pagination.total}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-sm text-slate-500">{filterProperty ? 'Active Leases in Property' : 'Active Leases'}</p><p className="text-2xl font-bold text-emerald-600">{activeLeases}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-sm text-slate-500">{filterProperty ? 'Monthly Revenue (Property)' : 'Monthly Revenue'}</p><p className="text-2xl font-bold text-indigo-600">{formatCurrency(totalMonthly)}</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-sm text-slate-500">Expiring in 90 Days</p><p className="text-2xl font-bold text-amber-600">{expiringSoon}</p></div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <select value={filterProperty} onChange={(e) => handleFilterChange('property', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => handleFilterChange('status', e.target.value)} className="px-4 py-2 border border-slate-300 rounded-lg">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
        <>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Property</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Term</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Monthly</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredLeases.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-500">No leases found</td></tr>
              ) : (
                filteredLeases.map((lease) => {
                  const daysLeft = getDaysUntilExpiry(lease.end_date);
                  return (
                    <tr key={lease.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <div><p className="font-medium text-slate-900">{lease.tenant_name}</p><p className="text-xs text-slate-500">{lease.tenant_email}</p></div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{lease.unit_number}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{lease.entity_name}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-900">{lease.start_date} - {lease.end_date}</p>
                        {daysLeft > 0 && daysLeft <= 90 && <p className="text-xs text-amber-600">{daysLeft} days remaining</p>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-semibold text-slate-900">{formatCurrency(lease.total_monthly)}</p>
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full capitalize ${lease.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{lease.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openViewModal(lease)} className="p-1 text-slate-600 hover:bg-slate-100 rounded" title="View"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEditModal(lease)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded" title="Edit"><Edit className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </>
        )}
      </div>

      {/* New/Edit Lease Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{editingLease ? 'Edit Lease' : 'New Lease'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                  <select required value={formData.entity_id} onChange={(e) => setFormData({...formData, entity_id: e.target.value, unit_id: ''})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="">Select property</option>
                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                  <select required value={formData.unit_id} onChange={(e) => setFormData({...formData, unit_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="">Select unit</option>
                    {units.filter(u => u.entity_id === formData.entity_id).map(u => <option key={u.id} value={u.id}>{u.unit_number}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Tenant Name</label>
                <input type="text" required value={formData.tenant_name} onChange={(e) => setFormData({...formData, tenant_name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="Full name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" required value={formData.tenant_email} onChange={(e) => setFormData({...formData, tenant_email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="tenant@email.com" />
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" value={formData.tenant_phone} onChange={(e) => setFormData({...formData, tenant_phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="(416) 555-1234" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Lease Type</label>
                  <select value={formData.lease_type} onChange={(e) => setFormData({...formData, lease_type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Monthly Amount</label>
                  <input type="number" required step="0.01" min="0" value={formData.monthly_rent} onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                  <input type="date" required value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                  <input type="date" required value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Security Deposit</label>
                <input type="number" step="0.01" min="0" value={formData.security_deposit} onChange={(e) => setFormData({...formData, security_deposit: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg" placeholder="0.00" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingLease ? 'Save Changes' : 'Create Lease'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Lease Modal */}
      {showViewModal && selectedLease && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Lease Agreement Details</h3>
              <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            
            <div className="border border-slate-200 rounded-xl p-6 space-y-6">
              {/* Header */}
              <div className="text-center border-b border-slate-200 pb-4">
                <h2 className="text-xl font-bold text-slate-900">RESIDENTIAL LEASE AGREEMENT</h2>
                <p className="text-sm text-slate-500 mt-1">Agreement #{selectedLease.id?.slice(0, 8).toUpperCase()}</p>
              </div>

              {/* Parties */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Landlord</h4>
                  <p className="text-slate-900 font-medium">{selectedLease.entity_name}</p>
                  <p className="text-sm text-slate-600">Property Management</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Tenant</h4>
                  <p className="text-slate-900 font-medium">{selectedLease.tenant_name}</p>
                  <p className="text-sm text-slate-600">{selectedLease.tenant_email}</p>
                  <p className="text-sm text-slate-600">{selectedLease.tenant_phone}</p>
                </div>
              </div>

              {/* Property Details */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Premises</h4>
                <p className="text-slate-900">Unit {selectedLease.unit_number}, {selectedLease.entity_name}</p>
              </div>

              {/* Lease Terms */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Lease Term</h4>
                  <p className="text-slate-900">Start: <span className="font-medium">{selectedLease.start_date}</span></p>
                  <p className="text-slate-900">End: <span className="font-medium">{selectedLease.end_date}</span></p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Rent Details</h4>
                  <p className="text-slate-900">Monthly: <span className="font-medium">{formatCurrency(selectedLease.monthly_rent || selectedLease.monthly_maintenance_fee)}</span></p>
                  <p className="text-slate-900">Total Monthly: <span className="font-medium">{formatCurrency(selectedLease.total_monthly)}</span></p>
                  {selectedLease.security_deposit > 0 && <p className="text-slate-900">Deposit: <span className="font-medium">{formatCurrency(selectedLease.security_deposit)}</span></p>}
                </div>
              </div>

              {/* Additional Fees */}
              {(selectedLease.parking_fee > 0 || selectedLease.storage_fee > 0) && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">Additional Fees</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLease.parking_fee > 0 && <p className="text-slate-900">Parking: {formatCurrency(selectedLease.parking_fee)}/month</p>}
                    {selectedLease.storage_fee > 0 && <p className="text-slate-900">Storage: {formatCurrency(selectedLease.storage_fee)}/month</p>}
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                <div>
                  <span className="text-sm text-slate-500">Status: </span>
                  <span className={`text-sm px-2 py-1 rounded-full capitalize ${selectedLease.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{selectedLease.status}</span>
                </div>
                <div>
                  <span className="text-sm text-slate-500">Type: </span>
                  <span className="text-sm font-medium text-slate-900 capitalize">{selectedLease.lease_type}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowViewModal(false); openEditModal(selectedLease); }} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">Edit Lease</button>
              <button onClick={() => setShowViewModal(false)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
